import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, CardHeader,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Spinner,
} from "reactstrap";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  Title, Tooltip, Legend, Filler,
  type ChartEvent, type ActiveElement,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";
import { useSeo } from "../hooks/useSeo";
import requests from "../requests";
import EdgeByConviction from "./EdgeByConviction";
import OutOfSampleValidation from "./OutOfSampleValidation";
import WeeklyScorecards from "./WeeklyScorecards";
import TrackRecordDiagnostics, { type Diagnostics } from "./TrackRecordDiagnostics";
import { useTheme } from "../context/ThemeContext";
import { WINDOW_LABELS } from "../utils/windows";
import { usePostHog } from "@posthog/react";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  Title, Tooltip, Legend, Filler,
);

interface WindowData {
  dates: string[];
  bull_win_rates: (number | null)[];
  bear_win_rates: (number | null)[];
  bull_avg_returns: (number | null)[];
  bear_avg_returns: (number | null)[];
  equity_curve: number[];
  diagnostics?: Diagnostics;
}

interface TrackRecordData {
  windows: Record<string, WindowData & { scored_count: number; pending_count: number }>;
  overall: {
    bullish_win_rate: number | null;
    bearish_win_rate: number | null;
    bullish_avg_return: number | null;
    bearish_avg_return: number | null;
    total_signals: number;
    total_signals_scored?: number;
    total_signals_pending?: number;
    methodology?: string;
    diagnostics?: Diagnostics;
    benchmark?: string;
  };
}

function pct(v: number | null) {
  return v !== null ? `${v.toFixed(1)}%` : "—";
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card className="text-center mb-3 process-card">
      <CardBody className="py-2">
        <div style={{ fontSize: 28, fontWeight: 700, color: "var(--primary)", fontFamily: "var(--font-mono)" }}>{value}</div>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{sub}</div>}
      </CardBody>
    </Card>
  );
}

export default function TrackRecord() {
  // Admin-only since 2026-07-31 (the API 401s for everyone else) — noindex so the page
  // stops ranking on the performance copy it used to carry.
  useSeo({
    title: "Track Record",
    description: "Internal signal-performance review.",
    path: "/track-record",
    noindex: true,
  });
  const [data, setData] = useState<TrackRecordData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("10");
  const [activeSection, setActiveSection] = useState("scorecards");
  const [activeValidation, setActiveValidation] = useState("diagnostics");
  const [startIdx, setStartIdx] = useState(0);   // day-0 index for the equity curve (0 = full history)
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const textMutedColor = isDark ? "#94A3B8" : "#64748B";
  const gridColor = isDark ? "rgba(148, 163, 184, 0.2)" : "rgba(203, 213, 225, 0.5)";
  const posthog = usePostHog();

  useEffect(() => {
    posthog.capture("track_record_viewed");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    requests.get("/api/track-record")
      .then(res => { setData(res.data); setLoading(false); })
      .catch((err: { response?: { status?: number } }) => {
        // 401/403 = not an admin. This page moved behind @admin_required on 2026-07-31
        // (no published edge over the matched null), so a non-admin hitting a stale
        // bookmark should see an explanation, not "data not yet available".
        const status = err.response?.status;
        setError(
          status === 401 || status === 403
            ? "admin-only"
            : "Track record data not yet available.",
        );
        setLoading(false);
      });
  }, []);

  // Reset the day-0 selection when the window changes (each window has its own dates).
  useEffect(() => { setStartIdx(0); }, [activeTab]);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner color="primary" />
      </Container>
    );
  }

  // Treat a missing/empty/malformed payload as "no data" — a 200 with {} or
  // {windows:{},overall:{}} (e.g. before the nightly archive is scored, or a
  // transient cache-load miss) otherwise reaches the destructure below and throws
  // (windows[tab] on undefined; pct(overall.bullish_win_rate) on undefined), which
  // the global ErrorBoundary would escalate to a whole-page blank.
  // data.overall/windows are typed non-null, but a 200 with {} (unscored archive /
  // cache miss) violates that at runtime — guard so the destructure below is safe.
  if (error === "admin-only") {
    return (
      <Container className="py-5 text-center" style={{ maxWidth: 640 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>This page is no longer public</h1>
        <p className="text-muted">
          Today&apos;s signals, each with the analogs behind it, are on the{" "}
          <a href="/featured">Featured page</a>.
        </p>
      </Container>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (error || !data || !data.overall || Object.keys(data.windows ?? {}).length === 0) {
    return (
      <Container className="py-5 text-center text-muted">
        <p>{error ?? "No data available."}</p>
      </Container>
    );
  }

  const { overall, windows } = data;
  // windows[key] is typed as always-defined by the Record index signature, but
  // a missing window key is undefined at runtime. Cast (not annotate) so the type
  // is genuinely `| undefined` and TS doesn't narrow it back — keeps guards valid.
  const wd = windows[activeTab] as
    | (WindowData & { scored_count: number; pending_count: number })
    | undefined;

  // Day-0 selection: rebase the equity curve to the chosen start day. sIdx is the index
  // within wd.dates the user clicked as "day 0" ($100 invested there); 0 = full history.
  // All Performance stats/charts below derive from the slice [sIdx:].
  const rawEquity = wd?.equity_curve ?? [];
  const sIdx = Math.min(startIdx, Math.max(0, rawEquity.length - 1));
  const base = rawEquity[sIdx] || 100;
  const sliceDates = wd ? wd.dates.slice(sIdx) : [];
  const sliceEquity = rawEquity.slice(sIdx).map(v => (base ? (v / base) * 100 : v));
  const sliceBull = wd ? wd.bull_win_rates.slice(sIdx) : [];
  const sliceBear = wd ? wd.bear_win_rates.slice(sIdx) : [];

  const mean = (arr: (number | null)[]): number | null => {
    const nums = arr.filter((r): r is number => r !== null);
    return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
  };
  const bullAvg = wd ? mean(sliceBull) : null;
  const bearAvg = wd ? mean(sliceBear) : null;
  const lastEquity = sliceEquity.length ? sliceEquity[sliceEquity.length - 1] : null;

  const equityChartData = wd ? {
    labels: sliceDates,
    datasets: [{
      label: "Equity Curve ($100 start)",
      data: sliceEquity,
      borderColor: "#3B82F6",
      backgroundColor: "rgba(59,130,246,0.08)",
      fill: true,
      tension: 0.3,
      // Solid, always-visible points so users can see what to hover; grow on hover.
      pointRadius: 3,
      pointHoverRadius: 6,
      pointBackgroundColor: "#3B82F6",
      pointBorderColor: "#3B82F6",
    }],
  } : null;

  const winRateChartData = wd ? {
    labels: sliceDates,
    datasets: [
      {
        label: "Bullish Win Rate %",
        data: sliceBull,
        backgroundColor: "rgba(5,150,105,0.65)",
        borderColor: "#059669",
        borderWidth: 1,
      },
      {
        label: "Bearish Win Rate %",
        data: sliceBear,
        backgroundColor: "rgba(220,38,38,0.65)",
        borderColor: "#DC2626",
        borderWidth: 1,
      },
    ],
  } : null;

  const lineOptions = {
    responsive: true,
    // Height is controlled by a fixed-height wrapper (below), not the width — otherwise
    // on a narrow phone the aspect ratio squashes the chart to ~90px and the line looks flat.
    maintainAspectRatio: false,
    plugins: { legend: { labels: { color: textMutedColor } } },
    scales: {
      x: { ticks: { color: textMutedColor, maxTicksLimit: 8 }, grid: { color: gridColor } },
      y: { ticks: { color: textMutedColor }, grid: { color: gridColor } },
    },
  };

  // Equity chart is clickable: pick any point as day 0 and the curve rebases to $100 there.
  const equityOptions = {
    ...lineOptions,
    interaction: { mode: "index" as const, intersect: false },
    onClick: (_evt: ChartEvent, els: ActiveElement[]) => {
      if (els.length) setStartIdx(sIdx + els[0].index);
    },
  };

  return (
    <Container className="py-4">
      <div className="d-flex align-items-center gap-2 mb-1">
        <h2 className="mb-0" style={{ fontWeight: 700 }}>Signal Track Record</h2>
        {overall.methodology === "actual_forward_returns" && (
          <span className="badge bg-success" style={{ fontSize: 12 }}>✓ Verified</span>
        )}
      </div>
      <p className="text-muted mb-4" style={{ fontSize: 15 }}>
        {overall.methodology === "actual_forward_returns"
          ? "Actual forward price outcomes for every featured signal. Win rate = stock moved in the predicted direction by the end of the forecast window."
          : "Historical analog statistics at time of signal issue."}
        {overall.total_signals_pending !== undefined && overall.total_signals_pending > 0 && (
          <span className="text-muted"> · {overall.total_signals_pending} signals still pending.</span>
        )}
      </p>

      {/* Overall summary — persistent above the section tabs */}
      <Row className="mb-4">
        <Col xs={6} md={3}><StatCard label="Bullish Win Rate" value={pct(overall.bullish_win_rate)} sub="avg across all windows" /></Col>
        <Col xs={6} md={3}><StatCard label="Bearish Win Rate" value={pct(overall.bearish_win_rate)} sub="avg across all windows" /></Col>
        <Col xs={6} md={3}><StatCard label="Avg Bullish Return" value={pct(overall.bullish_avg_return)} sub="forward period avg" /></Col>
        <Col xs={6} md={3}><StatCard
          label="Signals Scored"
          value={(overall.total_signals_scored ?? overall.total_signals).toLocaleString()}
          sub={overall.total_signals_pending ? `${overall.total_signals_pending} pending` : "all windows, all dates"}
        /></Col>
      </Row>

      {/* Section tabs — buyer-facing receipts first (Scorecards, Performance); the three
          rigor panels fold under one "Validation" tab (nested sub-tabs) so a general reader
          gets digestible proof up top and a skeptic finds full rigor one click deeper. */}
      <Nav tabs className="mb-0">
        {([
          { id: "scorecards", label: "Weekly Scorecards" },
          { id: "performance", label: "Performance" },
          { id: "validation", label: "Validation" },
        ]).map(s => (
          <NavItem key={s.id}>
            <NavLink
              active={activeSection === s.id}
              onClick={() => {
                if (s.id !== activeSection) posthog.capture("track_record_section_changed", { section: s.id });
                setActiveSection(s.id);
              }}
              style={{ cursor: "pointer", fontSize: 14, padding: "0.4rem 0.8rem", color: activeSection === s.id ? "var(--primary)" : "var(--text-muted)" }}
            >
              {s.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <TabContent activeTab={activeSection}>
        {/* Weekly Scorecards — the lead, buyer-facing receipt: top-conviction calls + the
            honest full-week hit rate. Renders nothing until /api/scorecards has data. */}
        <TabPane tabId="scorecards">
          <WeeklyScorecards />
        </TabPane>

        {/* Performance — per-window equity + win-rate charts (nested window selector) */}
        <TabPane tabId="performance">
          <Card>
            <CardHeader className="py-0 px-2" style={{ borderBottom: "1px solid var(--border-color)", background: "var(--card-background)" }}>
              <Nav tabs style={{ borderBottom: "none" }}>
                {Object.entries(WINDOW_LABELS).map(([key, label]) => (
                  <NavItem key={key}>
                    <NavLink
                      active={activeTab === key}
                      onClick={() => {
                        if (key !== activeTab) posthog.capture("track_record_window_changed", { window: key, window_label: label });
                        setActiveTab(key);
                      }}
                      style={{ cursor: "pointer", fontSize: 14, padding: "0.4rem 0.75rem", color: activeTab === key ? "var(--primary)" : "var(--text-muted)" }}
                    >
                      {label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </CardHeader>
            <CardBody className="pt-2">
              {wd !== undefined && equityChartData !== null && winRateChartData !== null ? (
                <TabContent activeTab={activeTab}>
                  <TabPane tabId={activeTab}>
                    <Row className="mb-3 text-center g-3">
                      <Col xs={6} md={3}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: "var(--success)", fontFamily: "var(--font-mono)" }}>{pct(bullAvg)}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>Avg Bullish Win Rate</div>
                      </Col>
                      <Col xs={6} md={3}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: "var(--error)", fontFamily: "var(--font-mono)" }}>{pct(bearAvg)}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>Avg Bearish Win Rate</div>
                      </Col>
                      <Col xs={6} md={3}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: "var(--primary)", fontFamily: "var(--font-mono)" }}>{sliceDates.length}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>Signal Days</div>
                      </Col>
                      <Col xs={6} md={3}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-mono)" }}>
                          {lastEquity !== null ? `$${lastEquity.toFixed(0)}` : "—"}
                        </div>
                        <div className="text-muted" style={{ fontSize: 12 }}>Equity ($100 start)</div>
                      </Col>
                    </Row>

                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3 mb-1">
                      <h6 className="mb-0 text-muted">
                        Equity Curve
                        {sIdx > 0 && sliceDates.length > 0 && (
                          <span style={{ fontWeight: 400 }}> — $100 invested {sliceDates[0]}</span>
                        )}
                      </h6>
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Click a point to set day 0
                        {sIdx > 0 && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 align-baseline"
                            onClick={() => setStartIdx(0)}
                          >
                            reset
                          </button>
                        )}
                      </span>
                    </div>
                    <div style={{ position: "relative", height: 220 }}>
                      <Line data={equityChartData} options={equityOptions} />
                    </div>

                    <h6 className="mt-4 mb-2 text-muted">Win Rate Over Time</h6>
                    <div style={{ position: "relative", height: 220 }}>
                      <Bar
                        data={winRateChartData}
                        options={{ ...lineOptions, scales: { ...lineOptions.scales, y: { ...lineOptions.scales.y, min: 0, max: 100 } } }}
                      />
                    </div>
                  </TabPane>
                </TabContent>
              ) : (
                <p className="text-muted text-center py-4">No data for this window.</p>
              )}
            </CardBody>
          </Card>
        </TabPane>

        {/* Validation — the three rigor panels folded under one tab (nested sub-tabs), one
            click deeper for skeptics: diagnostics vs. the right null, walk-forward /
            reality-check out-of-sample, and the strength-stratified conviction curve. */}
        <TabPane tabId="validation">
          <Nav tabs className="mb-3">
            {([
              { id: "diagnostics", label: "Diagnostics" },
              { id: "oos", label: "Out-of-Sample" },
              { id: "conviction", label: "By Conviction" },
            ]).map(v => (
              <NavItem key={v.id}>
                <NavLink
                  active={activeValidation === v.id}
                  onClick={() => {
                    if (v.id !== activeValidation) posthog.capture("track_record_validation_changed", { subsection: v.id });
                    setActiveValidation(v.id);
                  }}
                  style={{ cursor: "pointer", fontSize: 14, padding: "0.4rem 0.8rem", color: activeValidation === v.id ? "var(--primary)" : "var(--text-muted)" }}
                >
                  {v.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
          <TabContent activeTab={activeValidation}>
            <TabPane tabId="diagnostics">
              <TrackRecordDiagnostics data={data} />
            </TabPane>
            <TabPane tabId="oos">
              <OutOfSampleValidation />
            </TabPane>
            <TabPane tabId="conviction">
              <EdgeByConviction className="" />
            </TabPane>
          </TabContent>
        </TabPane>
      </TabContent>

      <p className="text-muted mt-3" style={{ fontSize: 12 }}>
        <strong>Methodology:</strong> Win rates and returns are computed from the distribution of forward prices
        across all historical analogs matched at the time each signal was issued. Past performance does not
        guarantee future results. Not financial advice.
      </p>
    </Container>
  );
}
